//old import { useEffect, useState } from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
//old import { Home, GraduationCap, HeartHandshake, CheckCircle2, CreditCard, Loader2 } from "lucide-react";
import { Home, GraduationCap, HeartHandshake, Medal, CheckCircle2, CreditCard, Loader2, FileText, Upload, X } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import api from "../../api/api";
import VerifyCodeForm from "./VerifyCodeForm";

/*
 * Community Partner sign-up (Alicia, Oct 16): nonprofits sign up here, apart
 * from schools and companies (/organizations/signup). Like that page it
 * creates a pending organization and an org admin login, verified with an
 * emailed code, and Pono Pilot approves it. It also asks what kind of
 * partner it is, a self-attested 501(c)(3) box and a package: 25 licenses
 * free, or 50 / 100 with the rest paid. A paid package saves a card after the
 * code (Stripe, nothing charged); the card is charged when the organization
 * is approved.
 * The 501(c)(3) proof comes with the form, or the box says it comes later, in
 * Settings (Alicia, Oct 22; JJ, Oct 23: one of the two is required).
 */

const KINDS = [
    { key: "housing", label: "Housing provider", icon: Home },
    { key: "training", label: "Training provider", icon: GraduationCap },
    { key: "rehabilitation", label: "Rehabilitation", icon: HeartHandshake },
    { key: "veteran", label: "Veteran services", icon: Medal }, // Alicia, Oct 21
];

const inputCls = (bad) => `w-full rounded-xl border bg-white p-3 text-slate-900 transition-colors ${bad ? "border-red-500" : "border-slate-300"}`;
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const per = (mode) => (mode === "monthly" ? "a month" : "a year");
const every = (mode) => (mode === "monthly" ? "every month" : "every year");
// The 501(c)(3) proof's rules, as the backend checks them (the Resources upload rules)
const PROOF_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg"];
const PROOF_MAX_BYTES = 20 * 1024 * 1024;
const proofProblem = (file) => {
    const name = (file.name || "").toLowerCase();
    if (!PROOF_EXTENSIONS.some((ext) => name.endsWith(ext))) return "Upload a PDF, PNG or JPG.";
    if (file.size === 0) return "That file is empty.";
    if (file.size > PROOF_MAX_BYTES) return "That file is over 20 MB.";
    return "";
};

export default function CommunityPartnerSignup() {
    const [params, setParams] = useSearchParams();
    // ?verify=<email>: signing in sends a sign-up whose emailed code was never entered back to that code
    const resumeEmail = (params.get("verify") || "").trim().toLowerCase();
    //old const [stage, setStage] = useState("form"); // form | verify | card | confirming | done
    const [stage, setStage] = useState(resumeEmail ? "verify" : "form"); // form | verify | card | confirming | done
    const [offer, setOffer] = useState(null);
    // Leads from the Go High Level campaign pages come with their name and email in the link
    // (?first_name=&last_name=&email=), so they don't type them again (Alicia, Oct 16)
    const [form, setForm] = useState(() => ({
        orgName: "", partnerType: "",
        firstName: (params.get("first_name") || "").trim().slice(0, 100),
        lastName: (params.get("last_name") || "").trim().slice(0, 100),
        //old email: (params.get("email") || "").trim().slice(0, 255),
        email: resumeEmail || (params.get("email") || "").trim().slice(0, 255),
        phone: "", password: "", attest501c3: false, package: 25, signupRequest: "",
        proof: null, // the 501(c)(3) proof file, when it comes with the form
        // its "We're a 501(c)(3) nonprofit" box, optional (Alicia, Oct 30): the proof part shows only once it's ticked
        is501c3: false,
    }));
    const [summary, setSummary] = useState(null); // the verified sign-up (package, price, card)
    const [showPassword, setShowPassword] = useState(false);
    //old const [errors, setErrors] = useState({});
    // which of check()'s messages show (below): all once Create organization was clicked, a field's once it was left
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [touched, setTouched] = useState({});
    const [serverError, setServerError] = useState("");
    // the email's sign-up was started before and never got its code: sign in to finish it
    const [finishBySignIn, setFinishBySignIn] = useState(false);
    // the sign-up this page sent (its orgId): sent again after 'Wrong email? Go back', the form takes its place
    const [sentOrgId, setSentOrgId] = useState(null);
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const touch = (k) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
    // A chosen proof is checked at once, so a wrong file shows its problem before the form is sent
    const pickProof = (file) => {
        setForm((f) => ({ ...f, proof: file }));
        //old setErrors((er) => ({ ...er, proof: file ? proofProblem(file) || undefined : undefined }));
        setTouched((t) => ({ ...t, proof: true }));
    };

    useEffect(() => {
        // if the packages can't load, the free one (no price needed) still works; the backend checks it again
        api.get("/org/signup/packages").then((res) => setOffer(res.data))
            .catch(() => setOffer({ packages: [{ licenses: 25, paidLicenses: 0, price: 0, perLicense: null, available: true }] }));
    }, []);

    // Back from Stripe: ?card_session= (card saved) or ?card=cancelled
    useEffect(() => {
        const cardSession = params.get("card_session");
        const cancelled = params.get("card") === "cancelled";
        if (!cardSession && !cancelled) return;
        setParams({}, { replace: true });
        if (cardSession) {
            setStage("confirming");
            api.post("/org/signup/card/confirm", { sessionId: cardSession })
                .then((res) => { setSummary(res.data); setStage("done"); })
                .catch((err) => {
                    setServerError(err.response?.data?.error || "We couldn't confirm your card. Try again.");
                    loadSummary("card");
                });
        } else {
            setNotice("Your card wasn't saved. You can add it now, or pay after we approve your organization.");
            loadSummary("card");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The server's message shows at the top of the card, far above Create organization: bring it into view
    const serverErrorRef = useRef(null);
    useEffect(() => {
        if (serverError) serverErrorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [serverError]);

    // fallback "done": the code was already verified, so a failed summary still finishes the sign-up
    const loadSummary = (next, fallback = "form") =>
        api.get("/org/signup/pending")
            .then((res) => { setSummary(res.data); setStage(typeof next === "function" ? next(res.data) : next); })
            .catch((err) => {
                if (fallback === "done") { setStage("done"); return; }
                setServerError(err.response?.data?.error || "We couldn't find your sign-up.");
                setStage("form");
            });

    const packages = offer?.packages || [];
    const chosen = packages.find((p) => p.licenses === Number(form.package));

    //old const validate = () => {
    const check = () => {
        const e = {};
        if (!form.partnerType) e.partnerType = "Pick what your organization is.";
        if (!form.orgName.trim()) e.orgName = "Enter your organization's name.";
        if (!form.firstName.trim()) e.firstName = "Required.";
        if (!form.lastName.trim()) e.lastName = "Required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email.";
        // Mirrors user_service.validate_password on the backend.
        if (form.password.length < 8 || !/[A-Z]/.test(form.password) || /\s/.test(form.password)) {
            e.password = "At least 8 characters with an uppercase letter, and no spaces.";
        }
        if (!chosen || !chosen.available) e.package = "Pick a package.";
        //old if (!form.attest501c3) e.attest501c3 = "Confirm that your organization is a 501(c)(3) nonprofit.";
        // the proof part only while the 501(c)(3) box is ticked: a file or 'later' picked before unticking it is
        // neither checked nor sent
        //old if (form.proof) {
        if (form.is501c3 && form.proof) {
            if (proofProblem(form.proof)) e.proof = proofProblem(form.proof);
        //old } else if (!form.attest501c3) {
        } else if (form.is501c3 && !form.attest501c3) {
            e.proof = "Upload your 501(c)(3) proof, or tick the box to upload it later.";
        }
        //old setErrors(e);
        //old return Object.keys(e).length === 0;
        return e;
    };
    // Checked as it's filled in (Alicia, Oct 30: real time checking for email and other fields), as on /login:
    // a field's problem shows once something is typed in it and it's left (a chosen proof at once), and every
    // problem once Create organization is clicked; a message goes as soon as its field is fixed
    const problems = check();
    const errors = Object.fromEntries(Object.entries(problems).filter(([k]) => isSubmitted || (touched[k] && form[k])));
    const validate = () => {
        setIsSubmitted(true);
        return Object.keys(problems).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        setServerError("");
        setFinishBySignIn(false);
        if (!validate()) return;
        setBusy(true);
        try {
            //old await api.post("/org/signup", {
            const body = {
                ...form,
                orgType: "nonprofit",
                email: form.email.trim().toLowerCase(),
                package: Number(form.package),
                //old attest501c3: true,
                //old attest501c3: form.attest501c3,
                attest501c3: form.is501c3 && form.attest501c3,
                signupRequest: form.signupRequest.trim() || undefined,
                proof: undefined,
                is501c3: undefined, // (what the API reads is attest501c3 and the file)
                // sent again after 'Wrong email? Go back': it replaces the sign-up sent before
                replacesOrgId: sentOrgId || undefined,
            //old });
            };
            let res;
            //old if (form.proof) {
            if (form.is501c3 && form.proof) {
                // with the proof the form goes as multipart: the fields as JSON in "data", the file in "proof"
                const data = new FormData();
                data.append("data", JSON.stringify(body));
                data.append("proof", form.proof);
                //old await api.post("/org/signup", data, { headers: { "Content-Type": undefined } });
                res = await api.post("/org/signup", data, { headers: { "Content-Type": undefined } });
            } else {
                //old await api.post("/org/signup", body);
                res = await api.post("/org/signup", body);
            }
            setSentOrgId(res.data.orgId);
            setStage("verify");
        } catch (err) {
            setServerError(err.response?.data?.error || "We couldn't create your organization. Please try again.");
            setFinishBySignIn(err.response?.data?.code === "SIGNUP_NOT_FINISHED");
        } finally {
            setBusy(false);
        }
    };

    // After the code: a paid package saves a card next
    //old const afterVerify = () => loadSummary((s) => (s.paidLicenses > 0 && s.paymentOpen ? "card" : "done"), "done");
    // Back from sign-in, ?verify= then leaves the address (once past the code step): a code works once,
    // so a refresh mustn't ask for it again
    const afterVerify = () => loadSummary((s) => (s.paidLicenses > 0 && s.paymentOpen ? "card" : "done"), "done")
        .then(() => { if (resumeEmail) setParams({}, { replace: true }); });

    const addCard = async () => {
        setServerError("");
        setBusy(true);
        try {
            const res = await api.post("/org/signup/card");
            window.location.assign(res.data.url);
        } catch (err) {
            setServerError(err.response?.data?.error || "We couldn't open the card page. Try again.");
            setBusy(false);
        }
    };

    const kindError = errors.partnerType;
    // no 501(c)(3) proof came with the form (the box said later): the done page says where it goes
    //old const proofLater = summary ? !summary.proofUploaded : !form.proof;
    // (only for one that said it's a 501(c)(3); a backend from before that sends no proofLater, and there every Community
    // Partner had said so)
    const proofLater = summary ? (summary.proofLater ?? !summary.proofUploaded) : form.is501c3 && !form.proof;
    return (
        <div className="min-h-screen bg-slate-50 text-slate-900">
            <main className="mx-auto max-w-2xl px-4 py-16">
                <div className="rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-2xl shadow-primary-200/20 sm:p-10">
                    {/* old {serverError && <p role="alert" className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{serverError}</p>} */}
                    {serverError && (
                        <p ref={serverErrorRef} role="alert" className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                            {serverError}
                            {finishBySignIn && <> <Link to="/login" className="font-semibold underline">Sign in</Link></>}
                        </p>
                    )}

                    {stage === "form" && (
                        <>
                            <h1 className="mb-2 text-3xl font-extrabold">Become a Community Partner</h1>
                            <p className="mb-8 text-slate-500">
                                {/* old Bring Pono Pilot to the people your nonprofit serves. Start free with 25 licenses. */}
                                Bring Pono Pilot to the people your nonprofit serves.
                                We'll review your request and get back to you.
                            </p>
                            <form onSubmit={submit} className="space-y-5" noValidate>
                                <fieldset>
                                    {/* old <legend className="mb-2 block text-sm font-semibold">Your organization is a</legend> */}
                                    {/* a red * after each required field's name (Alicia, Oct 30), as on /signup */}
                                    <legend className="mb-2 block text-sm font-semibold">Your organization is a<span className="ml-0.5 text-red-500">*</span></legend>
                                    {/* old <div className="grid gap-3 sm:grid-cols-3"> */}
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        {KINDS.map(({ key, label, icon: Icon }) => (
                                            <label key={key} className={`flex cursor-pointer items-center gap-2 rounded-2xl border-2 p-4 font-bold transition-colors ${form.partnerType === key ? "border-primary-500 bg-primary-50" : kindError ? "border-red-300" : "border-slate-200 hover:border-slate-300"}`}>
                                                <input type="radio" name="partnerType" value={key} checked={form.partnerType === key} onChange={set("partnerType")} className="sr-only" />
                                                <Icon size={18} /> {label}
                                            </label>
                                        ))}
                                    </div>
                                    {kindError && <p className="mt-1 text-xs text-red-600">{kindError}</p>}
                                </fieldset>
                                <div>
                                    {/* old <label htmlFor="cp-org" className="mb-1 block text-sm font-semibold">Organization name</label> */}
                                    <label htmlFor="cp-org" className="mb-1 block text-sm font-semibold">Organization name<span className="ml-0.5 text-red-500">*</span></label>
                                    {/* old <input id="cp-org" className={inputCls(errors.orgName)} value={form.orgName} onChange={set("orgName")} maxLength={200} /> */}
                                    <input id="cp-org" className={inputCls(errors.orgName)} value={form.orgName} onChange={set("orgName")} onBlur={touch("orgName")} maxLength={200} />
                                    {errors.orgName && <p className="mt-1 text-xs text-red-600">{errors.orgName}</p>}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        {/* old <label htmlFor="cp-first" className="mb-1 block text-sm font-semibold">Your first name</label> */}
                                        <label htmlFor="cp-first" className="mb-1 block text-sm font-semibold">Your first name<span className="ml-0.5 text-red-500">*</span></label>
                                        {/* old <input id="cp-first" className={inputCls(errors.firstName)} value={form.firstName} onChange={set("firstName")} maxLength={100} /> */}
                                        <input id="cp-first" className={inputCls(errors.firstName)} value={form.firstName} onChange={set("firstName")} onBlur={touch("firstName")} maxLength={100} />
                                        {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
                                    </div>
                                    <div>
                                        {/* old <label htmlFor="cp-last" className="mb-1 block text-sm font-semibold">Last name</label> */}
                                        <label htmlFor="cp-last" className="mb-1 block text-sm font-semibold">Last name<span className="ml-0.5 text-red-500">*</span></label>
                                        {/* old <input id="cp-last" className={inputCls(errors.lastName)} value={form.lastName} onChange={set("lastName")} maxLength={100} /> */}
                                        <input id="cp-last" className={inputCls(errors.lastName)} value={form.lastName} onChange={set("lastName")} onBlur={touch("lastName")} maxLength={100} />
                                        {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
                                    </div>
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div>
                                        {/* old <label htmlFor="cp-email" className="mb-1 block text-sm font-semibold">Work email</label> */}
                                        <label htmlFor="cp-email" className="mb-1 block text-sm font-semibold">Work email<span className="ml-0.5 text-red-500">*</span></label>
                                        {/* old <input id="cp-email" type="email" autoComplete="email" className={inputCls(errors.email)} value={form.email} onChange={set("email")} maxLength={255} /> */}
                                        <input id="cp-email" type="email" autoComplete="email" className={inputCls(errors.email)} value={form.email} onChange={set("email")} onBlur={touch("email")} maxLength={255} />
                                        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                                        <p className="mt-1 text-xs text-slate-500">Use an email that doesn't already have a Pono Pilot account.</p>
                                    </div>
                                    <div>
                                        <label htmlFor="cp-phone" className="mb-1 block text-sm font-semibold">Phone (optional)</label>
                                        <input id="cp-phone" type="tel" className={inputCls(false)} value={form.phone} onChange={set("phone")} maxLength={50} />
                                    </div>
                                </div>
                                <div>
                                    {/* old <label htmlFor="cp-password" className="mb-1 block text-sm font-semibold">Password</label> */}
                                    <label htmlFor="cp-password" className="mb-1 block text-sm font-semibold">Password<span className="ml-0.5 text-red-500">*</span></label>
                                    <div className="relative">
                                        <input id="cp-password" type={showPassword ? "text" : "password"} autoComplete="new-password"
                                               //old className={`${inputCls(errors.password)} pr-11`} value={form.password} onChange={set("password")} />
                                               className={`${inputCls(errors.password)} pr-11`} value={form.password} onChange={set("password")} onBlur={touch("password")} />
                                        <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                                            {showPassword ? <FiEyeOff /> : <FiEye />}
                                        </button>
                                    </div>
                                    {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
                                </div>

                                <fieldset>
                                    <legend className="mb-2 block text-sm font-semibold">Licenses</legend>
                                    {!offer ? (
                                        <div className="grid gap-3 sm:grid-cols-3">
                                            {[0, 1, 2].map((i) => <Skeleton key={i} height={80} borderRadius={16} />)}
                                        </div>
                                    ) : (
                                        <div className="grid gap-3 sm:grid-cols-3">
                                            {packages.map((p) => {
                                                const picked = Number(form.package) === p.licenses;
                                                return (
                                                    <label key={p.licenses} className={`rounded-2xl border-2 p-4 transition-colors ${!p.available ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400" : picked ? "cursor-pointer border-primary-500 bg-primary-50" : "cursor-pointer border-slate-200 hover:border-slate-300"}`}>
                                                        <input type="radio" name="package" value={p.licenses} checked={picked} disabled={!p.available}
                                                               onChange={() => setForm((f) => ({ ...f, package: p.licenses }))} className="sr-only" />
                                                        <span className="block text-lg font-extrabold">{p.licenses} licenses</span>
                                                        <span className="mt-1 block text-sm">
                                                            {p.paidLicenses === 0 ? "Free" : p.available ? <>{money(p.price)} {per(offer.billingMode)}</> : "Online payment opens soon"}
                                                        </span>
                                                        {p.available && p.perLicense != null && (
                                                            <>
                                                            {/* old <span className="mt-0.5 block text-xs text-slate-500">{p.paidLicenses} paid × {money(p.perLicense)}</span> */}
                                                            <span className="mt-0.5 block text-xs text-slate-500">{p.licenses - p.paidLicenses} free + {p.paidLicenses} × {money(p.perLicense)}</span>
                                                            </>
                                                        )}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {errors.package && <p className="mt-1 text-xs text-red-600">{errors.package}</p>}
                                    <p className="mt-2 text-xs text-slate-500">
                                        Your first 25 licenses are free. A paid package is charged only when we approve your organization,
                                        then renews {every(offer?.billingMode)} until you cancel.
                                    </p>
                                </fieldset>

                                <div>
                                    {/* Saying it's a 501(c)(3) is optional (Alicia, Oct 30: "make checkbox optional"; training and housing
                                        providers may not be nonprofits), with a box like the one under it; the proof part shows only once
                                        it's ticked, indented under it (Evan: the proof step only for one that says it is) */}
                                    <label className="flex items-start gap-3 text-sm">
                                        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.is501c3}
                                               onChange={(e) => setForm((f) => ({ ...f, is501c3: e.target.checked }))} />
                                        <span>We're a 501(c)(3) nonprofit</span>
                                    </label>
                                    {form.is501c3 && (
                                    <div className="mt-3 pl-7">
                                    {/* the 501(c)(3) proof: with the form, or the box says it comes later, in Settings */}
                                    {/* old <p className="mb-1 text-sm font-semibold">501(c)(3) proof</p> */}
                                    {/* (shown only once 'We're a 501(c)(3) nonprofit' is ticked, and then a file or the 'later' box is
                                        needed: a red * as on the other required fields) */}
                                    <p className="mb-1 text-sm font-semibold">501(c)(3) proof<span className="ml-0.5 text-red-500">*</span></p>
                                    <p className="mb-2 text-xs text-slate-500">Your IRS determination letter or other proof that your organization is a 501(c)(3) nonprofit. PDF, PNG or JPG, up to 20 MB.</p>
                                    {form.proof ? (
                                        <div className={`mb-3 flex items-center gap-2 rounded-xl border bg-slate-50 px-3 py-2 text-sm ${errors.proof ? "border-red-500" : "border-slate-300"}`}>
                                            <FileText size={16} className="shrink-0 text-slate-500" />
                                            <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{form.proof.name}</span>
                                            <button type="button" onClick={() => pickProof(null)} aria-label="Remove the file"
                                                    className="shrink-0 rounded-full p-1 text-slate-500 hover:bg-slate-200">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="mb-3 inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-within:ring-2 focus-within:ring-indigo-400">
                                            <Upload size={16} /> Choose file
                                            <input type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="sr-only"
                                                   onChange={(e) => { const file = e.target.files?.[0] || null; e.target.value = ""; pickProof(file); }} />
                                        </label>
                                    )}
                                    <label className="flex items-start gap-3 text-sm">
                                        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.attest501c3}
                                               onChange={(e) => setForm((f) => ({ ...f, attest501c3: e.target.checked }))} />
                                        {/* old <span>We are a 501(c)(3) nonprofit organization.</span> */}
                                        {/* old <span>We're a 501(c)(3) nonprofit and will upload our proof later, in Settings.</span> */}
                                        {/* (the box above already says it's a 501(c)(3)) */}
                                        <span>We'll upload our proof later, in Settings.</span>
                                    </label>
                                    {/* old {errors.attest501c3 && <p className="mt-1 text-xs text-red-600">{errors.attest501c3}</p>} */}
                                    {errors.proof && <p className="mt-1 text-xs text-red-600">{errors.proof}</p>}
                                    </div>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="cp-note" className="mb-1 block text-sm font-semibold">Anything we should know? (optional)</label>
                                    <textarea id="cp-note" rows={3} maxLength={1000} className={`${inputCls(false)} resize-none`}
                                              value={form.signupRequest} onChange={set("signupRequest")} />
                                </div>
                                <button type="submit" disabled={busy}
                                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-700 disabled:opacity-60">
                                    {busy && <Loader2 size={16} className="animate-spin" />} Create organization
                                </button>
                            </form>
                            <p className="mt-6 text-center text-sm text-slate-600">
                                A school or company? <Link to="/organizations/signup" className="font-semibold text-primary-600 hover:text-primary-700">Sign up your organization here</Link>
                            </p>
                        </>
                    )}

                    {stage === "verify" && (
                        <VerifyCodeForm
                            email={form.email.trim().toLowerCase()}
                            onVerified={afterVerify}
                            verifyEndpoint="/org/signup/verify"
                            resendEndpoint="/org/signup/resend"
                            onChangeEmail={resumeEmail ? undefined : () => setStage("form")}
                        />
                    )}

                    {stage === "confirming" && (
                        <div className="flex flex-col items-center gap-3 py-10 text-slate-500">
                            <Loader2 className="animate-spin" size={28} />
                            <p>Saving your card…</p>
                        </div>
                    )}

                    {stage === "card" && summary && (
                        <div className="text-center">
                            <CreditCard size={44} className="mx-auto mb-4 text-primary-600" />
                            <h1 className="mb-2 text-3xl font-extrabold">Add a card for your package</h1>
                            {notice && <p role="status" className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-800">{notice}</p>}
                            <p className="mb-8 text-slate-500">
                                {/* old Your {summary.package} licenses include {summary.paidLicenses} paid ones: {money(summary.price)} {per(summary.billingMode)}. */}
                                {/* the package as the Licenses window names it: 50 licenses (25 free + 25 paid) */}
                                Your package: {summary.package} licenses ({summary.package - summary.paidLicenses} free + {summary.paidLicenses} paid), {money(summary.price)} {per(summary.billingMode)}.
                                Nothing is charged now. When we approve {summary.orgName}, we charge this card {money(summary.price)},
                                then again {every(summary.billingMode)} until you cancel, which you can do anytime from your Licenses page.
                            </p>
                            <button type="button" onClick={addCard} disabled={busy}
                                    className="mx-auto flex w-full max-w-sm items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:bg-primary-700 disabled:opacity-60">
                                {busy && <Loader2 size={16} className="animate-spin" />} Add card
                            </button>
                            <button type="button" onClick={() => setStage("done")} className="mt-4 text-sm font-semibold text-slate-500 hover:text-slate-700">
                                Skip for now and pay after approval
                            </button>
                        </div>
                    )}

                    {stage === "done" && (
                        <div className="text-center">
                            <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-500" />
                            {summary?.status === "active" ? (
                                <>
                                    <h1 className="mb-2 text-3xl font-extrabold">You're approved</h1>
                                    <p className="mb-4 text-slate-500">
                                        {/* old {summary.orgName} is already approved. Sign in with {summary.email} to create programs and invite the people you serve. */}
                                        {/* (not 'create programs': its default program, made the first time its admin opens the portal, is a
                                            Community Partner's one program; the Overview's step 1 says 'Check your programs.') */}
                                        {summary.orgName} is already approved. Sign in with {summary.email} to check your programs and add the people you serve.
                                    </p>
                                    {proofLater && <p className="mb-4 text-slate-500">Upload your 501(c)(3) proof in Settings.</p>}
                                    {summary.paidLicenses > 0 && (
                                        //old <p className="mb-8 text-slate-500">Pay for your {summary.paidLicenses} extra licenses on your Licenses page.</p>
                                        <p className="mb-8 text-slate-500">Pay for your {summary.paidLicenses} paid licenses on your Licenses page.</p>
                                    )}
                                </>
                            ) : (
                                <>
                                    <h1 className="mb-2 text-3xl font-extrabold">Request received</h1>
                                    <p className="mb-4 text-slate-500">
                                        Thanks! Our team will review {summary?.orgName || form.orgName} and email you when it's approved.
                                        {/* old Then sign in with {summary?.email || form.email} to create programs and invite the people you serve. */}
                                        {" "}Then sign in with {summary?.email || form.email} to check your programs and add the people you serve.
                                    </p>
                                    {proofLater && <p className="mb-4 text-slate-500">Once you're approved, upload your 501(c)(3) proof in Settings.</p>}
                                    {summary?.paidLicenses > 0 && (
                                        <p className="mb-8 text-slate-500">
                                            {summary.cardSaved
                                                //old ? <>When we approve it, we'll charge your card {money(summary.price)} for {summary.paidLicenses} more licenses,
                                                ? <>When we approve it, we'll charge your card {money(summary.price)} for {summary.package} licenses
                                                    {" "}({summary.package - summary.paidLicenses} free + {summary.paidLicenses} paid),
                                                    then again {every(summary.billingMode)} until you cancel, anytime from your Licenses page.</>
                                                //old : <>Once it's approved, pay for your {summary.paidLicenses} extra licenses on your Licenses page. Until then you have 25 free licenses.</>}
                                                : <>Once it's approved, pay for your {summary.paidLicenses} paid licenses on your Licenses page. Until then you have 25 free licenses.</>}
                                        </p>
                                    )}
                                </>
                            )}
                            <Link to="/" className="font-semibold text-primary-600 hover:text-primary-700">Back to home</Link>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
